import { derivedDueDate } from '../../lib/masterdata/payment-terms.presentation';
import { shipmentReference } from '../../lib/warehouse/presentation';
import { DerivationPanel } from './DerivationPanel';
import { Link } from 'react-router-dom';
import { FifoEvidence } from './FifoEvidence';
import { StatusBadge } from '../operations/StatusBadge';
import { label, scalar } from '../../lib/workflows/forms';
import {
  isOpaque,
  isReference,
  joinedReference,
  referenceKind,
  referenceTitle,
} from '../../lib/workflows/reference.presentation';
import { ReferenceDetails } from '../operations/ReferenceDetails';
import { text, object } from '../../lib/workflows/repository';
import type { Row, Value } from '../../lib/workflows/types';
import { isBankField, isProtectedField } from '../../lib/protected-fields';
import { useAccess } from '../../lib/access/access.context';

const statusKeys = new Set([
  'status',
  'verdict',
  'outcome',
  'stock_status',
  'lot_match',
  'temperature_status',
]);
export function Evidence({
  value,
  name = '',
  depth = 0,
  context = {},
  record = context,
  domain = '',
}: {
  value: Value | undefined;
  name?: string;
  depth?: number;
  context?: Row;
  record?: Row;
  domain?: string;
}) {
  const access = useAccess();
  if (name === 'shipment_reference') return <span>{shipmentReference(value)}</span>;
  if (isBankField(name) && !access.allows('billing:bank:read')) return <span>Restricted</span>;
  if (isProtectedField(name) && value == null) return <span>Restricted</span>;
  if (name === 'derived_due_date' || (domain === 'billing' && name === 'due_date')) return <span>{derivedDueDate(value, record.status === 'not_evaluated' ? record.status : undefined)}</span>;
  if (name === 'current_value' && (record.is_default === true || value === null))
    return <span>Not configured{value === null ? '' : ` · default ${String(value)}`}</span>;
  if (name.endsWith('_derivation') && value !== undefined && value !== null)
    return <DerivationPanel title={label(name)} value={value} />;
  if ((name === 'rule_code' || name === 'rule_codes') && typeof value === 'string')
    return (
      <span>
        {value.replace(/^VAL_/, '').replaceAll('_', ' ').toLowerCase()} <code>({value})</code>
      </span>
    );
  if (
    ['lines', 'rows', 'tokens', 'dates', 'warnings'].includes(name) &&
    Array.isArray(value) &&
    value.length &&
    value.every((item) => item !== null && typeof item === 'object' && !Array.isArray(item))
  ) {
    const keys = [...new Set(value.flatMap((item) => Object.keys(object(item))))];
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-left text-body">
          <thead>
            <tr>
              {keys.map((key) => (
                <th className="p-2" key={key}>
                  {label(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {value.map((item, index) => (
              <tr key={index}>
                {keys.map((key) => (
                  <td className="border-t border-border p-2 align-top" key={key}>
                    <Evidence
                      value={object(item)[key]}
                      name={key}
                      depth={depth + 1}
                      context={{ ...context, ...object(item) }}
                      record={object(item)}
                      domain={domain}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  const diagnostic = object(value);
  if (diagnostic.product_code && Array.isArray(diagnostic.missing) && 'base_uom' in diagnostic)
    return (
      <div className="space-y-3 rounded-field bg-warning-bg p-4">
        <h4 className="text-section font-semibold">
          Packaging setup needed · {text(diagnostic.product_code)}
        </h4>
        <p className="text-body">
          Base unit: {text(diagnostic.base_uom) || 'Not recorded'}. Add effective conversions for
          the departure date:
        </p>
        <ul className="list-inside list-disc text-body">
          {diagnostic.missing.map((item, index) => (
            <li key={index}>{text(item)}</li>
          ))}
        </ul>
        <Link className="text-label text-primary underline" to="/admin/masterdata/products">
          Open products → Packaging &amp; units
        </Link>
      </div>
    );
  if (name === 'fifo_check') return <FifoEvidence value={value ?? null} />;
  if (
    value !== null &&
    value !== undefined &&
    value !== '' &&
    (isReference(name) || isOpaque(value))
  ) {
    const joined = joinedReference(name, record);
    const kind = referenceKind(name, domain);
    if (joined) return <span>{joined}</span>;
    if (Array.isArray(value))
      return (
        <div className="space-y-2">
          {value.map((item, index) => (
            <Evidence
              domain={domain}
              key={index}
              name={name}
              value={item}
              context={context}
              record={record}
              depth={depth + 1}
            />
          ))}
        </div>
      );
    if (typeof value === 'string')
      return kind ? (
        <ReferenceDetails kind={kind} id={value} scope={text(context.shipment_id)} />
      ) : (
        <span>Details unavailable</span>
      );
  }
  if (value === null || typeof value !== 'object')
    return (
      <span className="break-words [overflow-wrap:anywhere]">
        {statusKeys.has(name) && typeof value === 'string' ? (
          <StatusBadge status={value} />
        ) : (
          scalar(value)
        )}
      </span>
    );
  if (Array.isArray(value))
    return value.length ? (
      <div className="space-y-3">
        {value.map((entry, index) => (
          <div
            key={index}
            className={typeof entry === 'object' ? 'rounded-field border border-border p-3' : ''}
          >
            <Evidence
              domain={domain}
              value={entry}
              name={name}
              depth={depth + 1}
              context={context}
            />
          </div>
        ))}
      </div>
    ) : (
      <p className="text-body text-muted-foreground">None recorded.</p>
    );
  const entries = Object.entries(object(value));
  const own = object(value);
  const local = { ...context, ...own };
  const technical = entries.filter(
    ([key, item]) => key === 'id' || isReference(key) || isOpaque(item)
  );
  const facts = entries.filter(
    ([key, item]) =>
      key !== 'id' &&
      (item === null || typeof item !== 'object') &&
      !(isOpaque(item) && !isReference(key))
  );
  const nested = entries.filter(([, item]) => item !== null && typeof item === 'object');
  return (
    <div className="space-y-4">
      {!!facts.length && (
        <dl className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {facts.map(([key, item]) => (
            <div key={key} className="min-w-0">
              <dt className="text-label font-semibold text-muted-700">
                {key === 'location_id' && own.reason_code === 'TAXED_LINE_NO_TAX_RATE'
                  ? 'Delivery site'
                  : isReference(key)
                    ? referenceTitle(key)
                    : label(key)}
              </dt>
              <dd className="mt-1 text-body">
                <Evidence
                  domain={domain}
                  value={item}
                  name={isProtectedField(name) && ['before', 'after', 'held', 'presented'].includes(key) ? name : key}
                  depth={depth + 1}
                  context={local}
                  record={own}
                />
              </dd>
            </div>
          ))}
        </dl>
      )}
      {!!technical.length && (
        <details className="rounded-field border border-border p-3">
          <summary className="cursor-pointer text-label text-muted-foreground">
            Technical references
          </summary>
          <dl className="mt-3 space-y-2">
            {technical.map(([key, item]) => (
              <div key={key}>
                <dt className="text-micro text-muted-foreground">{label(key)}</dt>
                <dd className="break-all font-mono text-micro">
                  {Array.isArray(item) ? item.map(text).join(', ') : text(item) || 'Not recorded'}
                </dd>
              </div>
            ))}
          </dl>
        </details>
      )}
      {nested.map(([key, item]) => (
        <details key={key} open={depth < 1} className="rounded-field border border-border p-3">
          <summary className="cursor-pointer text-label font-semibold">
            {label(key)}
            {Array.isArray(item) ? ` · ${item.length}` : ''}
          </summary>
          <div className="mt-3">
            <Evidence
              domain={domain}
              value={item}
              name={key}
              depth={depth + 1}
              context={local}
              record={own}
            />
          </div>
        </details>
      ))}
    </div>
  );
}
